import {
  CommandBar,
  Text,
  DetailsListLayoutMode,
  ICommandBarItemProps,
  IconButton,
  SearchBox,
  SelectionMode,
  Stack,
  IColumn,
  FontIcon,
  mergeStyles,
  ConstrainMode,
  VerticalDivider,
} from '@fluentui/react';
import { Input } from '@fluentui/react-components';
import { Dismiss20Regular, Search20Regular } from '@fluentui/react-icons';
import {
  DetailsList,
  IDetailsColumnProps,
  Selection,
  IDetailsHeaderProps,
} from '@fluentui/react/lib/DetailsList';
import { IRenderFunction } from '@fluentui/react/lib/Utilities';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { IDetailsColumnRenderTooltipProps } from '@fluentui/react/lib/DetailsList';
import { enrichItemWithOtherInfo } from '@moko/utils';
import { useContext, useEffect, useImperativeHandle, useState } from 'react';
import AppContext from '../components/AppContext';
import { IPagedCollection } from '../services/assetServices';

import { useSetAtom } from 'jotai';
import { errorMessageAtom, isInProgressAtom } from '../atoms/messageBarAtoms';
import React from 'react';
import {
  focusZoneProps,
  gridStyles,
  gridStylesNames,
} from './styles/GridStyles';
import LoadingSign from './LoadingSign';

export interface IListPageColumn {
  key: string;
  name?: string;
  minWidth?: number;
  maxWidth?: number;
  isResizable?: boolean;
  onRender?: (
    item?: any,
    index?: number | undefined,
    column?: IColumn | undefined
  ) => any;
  isSortable?: boolean;
}

export interface IListPageParams {
  selectedTab: string;
  selectionMode?: SelectionMode;
  columns: IListPageColumn[];
  commandBarItems: ICommandBarItemProps[];
  commandBarLeadingContent?: React.ReactNode;
  commandBarOverflowItems?: ICommandBarItemProps[];
  toolbarWidth?: number | string;
  searchInToolbar?: boolean;
  searchInputWidth?: number | string;
  getAction: (
    abortController: AbortController,
    pageSize: number,
    pageNo: number,
    orderBy?: string,
    search?: string
  ) => Promise<IPagedCollection<any>>;
  exportAction?: () => void;
  onSelectionChange?: (selection: any) => void;
  onRowDoubleClicked?: (selection: any) => void;
  compactMode?: boolean;
  pageSize?: number;
  listHeigh?: number;
  disabled?: boolean;
  detailsListWidth?: number;
  hideCommandBar?: boolean;
  otherInfoFieldNames?: string[];
  loadingMessage?: string;
  loadingIconName?: string;
}

export interface IPagedListRefType {
  refresh: () => void;
}

const PagedList: React.ForwardRefRenderFunction<
  IPagedListRefType,
  IListPageParams
> = (params, forwardedRef) => {
  useImperativeHandle(forwardedRef, () => ({ refresh }));
  const refresh = async () => {
    setRefreshCount(refreshCount + 1);
  };
  const context = useContext(AppContext);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const [isLastPage, setIsLastPage] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState({ no: 1, search: '', filters: '' });
  const [searchValue, setSearchValue] = useState('');
  const [orderBy, setOrderBy] = useState<string>();
  const [refreshCount, setRefreshCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [items, setItems] = useState<any[]>();

  useEffect(() => {
    const fetchData = async () => {
      context.setSelectedTab(params.selectedTab);
      const abortController = new AbortController();
      setIsInProgress(true);
      try {
        const data: IPagedCollection<any> = await params.getAction(
          abortController,
          params.pageSize ?? 10,
          page.no,
          orderBy,
          page.search
        );
        setItems(
          data?.items.map((item) =>
            params.otherInfoFieldNames
              ? enrichItemWithOtherInfo(item, params.otherInfoFieldNames)
              : item
          )
        );
        setIsLastPage(data?.isLastPage);
        setPageCount(data?.pageCount);
        setLoading(false);
      } catch (error: any) {
        console.error('Error:', error);
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
      return () => {
        abortController.abort();
      };
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page.no,
    page.search,
    page.filters,
    refreshCount,
    params.otherInfoFieldNames,
    orderBy,
  ]);

  const sortIconClass = mergeStyles({
    fontSize: 12,
    paddingLeft: 5,
  });

  const renderHeaderSortable = (
    props?: IDetailsColumnProps,
    defaultRender?: (props?: IDetailsColumnProps) => React.ReactElement | null
  ): React.ReactElement | null =>
    !defaultRender ? (
      <></>
    ) : (
      <Stack horizontal verticalAlign="center">
        {defaultRender(props)}
        {!props?.column.isSorted && (
          <FontIcon
            aria-label="Sortable"
            iconName="Sort"
            className={sortIconClass}
          />
        )}
      </Stack>
    );

  const sortableColumnClicked = (
    _ev: React.MouseEvent<HTMLElement, MouseEvent>,
    column: IColumn
  ): void | undefined => {
    if (orderBy === column.fieldName) {
      setOrderBy(`${column.fieldName} Desc`);
      return;
    }

    setOrderBy(column.fieldName);
  };

  const _selection = new Selection({
    onSelectionChanged: () => {
      if (params.onSelectionChange) {
        if (_selection.getSelectedCount() > 0) {
          params.onSelectionChange(_selection.getSelection()[0]);
        }
      }
    },
    canSelectItem: (): boolean => {
      if (params.disabled) {
        return false;
      }
      return true;
    },
  });

  const onItemInvoked = (item: any) => {
    if (params.onRowDoubleClicked) {
      params.onRowDoubleClicked(item);
    }
  };

  const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (
    props,
    defaultRender
  ) => {
    if (!props) {
      return null;
    }
    const onRenderColumnHeaderTooltip: IRenderFunction<
      IDetailsColumnRenderTooltipProps
    > = (tooltipHostProps) => <TooltipHost {...tooltipHostProps} />;
    return defaultRender!({
      ...props,
      onRenderColumnHeaderTooltip,
    });
  };

  const refreshButton: ICommandBarItemProps = {
    key: 'refresh',
    text: 'Refresh',
    iconProps: { iconName: 'Refresh' },
    onClick: () => {
      setRefreshCount(refreshCount + 1);
    },
  };

  const exportButton: ICommandBarItemProps = {
    key: 'export',
    text: 'Export',
    iconProps: { iconName: 'CloudDownload' },
    onClick: () => {
      if (params.exportAction) {
        params.exportAction();
      }
    },
  };

  const handleSearch = () => {
    setPage((prevPage) => ({ ...prevPage, no: 1, search: searchValue }));
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setPage((prevPage) => ({ ...prevPage, no: 1, search: '' }));
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {!params.hideCommandBar && (
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: 8 }}
          styles={{ root: { flexShrink: 0 } }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              ...(params.toolbarWidth
                ? {
                    width: params.toolbarWidth,
                    minWidth: params.toolbarWidth,
                    maxWidth: params.toolbarWidth,
                  }
                : {
                    flexGrow: 1,
                    minWidth: 0,
                  }),
            }}
          >
            {params.commandBarLeadingContent ?? null}
            <div
              style={
                params.searchInToolbar
                  ? {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexShrink: 0,
                    }
                  : { flexGrow: 1, minWidth: 0 }
              }
            >
              <CommandBar
                items={[
                  refreshButton,
                  ...params.commandBarItems,
                  ...(params.exportAction ? [exportButton] : []),
                ]}
                overflowItems={params.commandBarOverflowItems}
                styles={
                  params.searchInToolbar
                    ? {
                        root: {
                          width: 'auto',
                        },
                      }
                    : undefined
                }
                ariaLabel="Items actions"
                primaryGroupAriaLabel="Items actions"
                farItemsGroupAriaLabel="More actions"
              />
              {params.searchInToolbar ? (
                <>
                  <VerticalDivider />
                  <Input
                    placeholder="Search"
                    style={{
                      width: params.searchInputWidth ?? 260,
                      minWidth: params.searchInputWidth ?? 260,
                    }}
                    value={searchValue}
                    onChange={(_event, data) => setSearchValue(data.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    contentBefore={<Search20Regular />}
                    contentAfter={
                      searchValue ? (
                        <Dismiss20Regular
                          style={{ cursor: 'pointer' }}
                          onClick={handleClearSearch}
                        />
                      ) : undefined
                    }
                    disabled={params.disabled}
                  />
                </>
              ) : null}
            </div>
          </div>
          {!params.searchInToolbar ? (
            <SearchBox
              placeholder="Search"
              onSearch={(newValue) => {
                setSearchValue(newValue || '');
                setPage((prevPage) => ({
                  ...prevPage,
                  no: 1,
                  search: newValue || '',
                }));
              }}
              onClear={handleClearSearch}
              disabled={params.disabled}
            />
          ) : null}
        </Stack>
      )}
      <div>
        {loading ? (
          <LoadingSign
            message={params.loadingMessage || 'Loading ...'}
            iconName={params.loadingIconName || 'CubeShape'}
          />
        ) : (
          <DetailsList
            items={items ?? []}
            columns={params.columns.map((c) => ({
              key: c.key,
              name: c.name ?? c.key,
              fieldName: c.key,
              minWidth: c.minWidth ?? 100,
              maxWidth: c.maxWidth ?? 125,
              isResizable: c.isResizable ?? true,
              onRender: c.onRender,
              onRenderHeader: c.isSortable ? renderHeaderSortable : undefined,
              onColumnClick: c.isSortable ? sortableColumnClicked : undefined,
              isSorted: c.isSortable ? orderBy?.startsWith(c.key) : false,
              isSortedDescending: c.isSortable
                ? orderBy === `${c.key} Desc`
                : false,
            }))}
            setKey="set"
            layoutMode={DetailsListLayoutMode.fixedColumns}
            constrainMode={ConstrainMode.unconstrained}
            selectionMode={params.selectionMode ?? SelectionMode.single}
            onItemInvoked={onItemInvoked}
            selection={_selection}
            selectionPreservedOnEmptyClick={true}
            ariaLabelForSelectionColumn="Toggle selection"
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            checkButtonAriaLabel="select row"
            compact={params.compactMode}
            onRenderDetailsHeader={onRenderDetailsHeader}
            styles={gridStyles}
            focusZoneProps={focusZoneProps}
            selectionZoneProps={{
              className: gridStylesNames.selectionZone,
            }}
          />
        )}
      </div>
      <Stack horizontal horizontalAlign="space-between">
        <Stack.Item grow={1} align="center">
          <IconButton
            iconProps={{ iconName: 'DoubleChevronLeft' }}
            disabled={page.no === 1}
            onClick={() => setPage({ ...page, no: 1 })}
          />
          <IconButton
            iconProps={{ iconName: 'ChevronLeft' }}
            disabled={page.no === 1}
            onClick={() => setPage({ ...page, no: page.no - 1 })}
          />
          <Text>
            {page.no} of {pageCount}
          </Text>
          <IconButton
            iconProps={{ iconName: 'ChevronRight' }}
            disabled={isLastPage}
            onClick={() => setPage({ ...page, no: page.no + 1 })}
          />
          <IconButton
            iconProps={{ iconName: 'DoubleChevronRight' }}
            disabled={isLastPage}
            onClick={() => setPage({ ...page, no: pageCount })}
          />
        </Stack.Item>
      </Stack>
    </div>
  );
};

export default React.forwardRef(PagedList);
